export enum LibraryType {
    SYSTEM = 'SYSTEM',
    COMMUNITY = 'COMMUNITY'
}

export enum LibraryItemType {
    SINGLE = 'SINGLE',
    GROUP = 'GROUP'
}

export enum LibraryTier {
    FREE = 'FREE',
    PREMIUM = 'PREMIUM',
}

export const LIBRARY_TIER_LABELS: Record<LibraryTier, string> = {
    [LibraryTier.FREE]: '무료',
    [LibraryTier.PREMIUM]: '프리미엄',
};

export interface PreviewWord {
    word: string;
    meaning: string;
}

export interface GroupWordbookInfo {
    wordbookId: number;
    title: string;
    wordCount: number;
    thumbnailUrl?: string;
}

// ── 라이브러리 섹션 (CATEGORY / CURATION / DYNAMIC 통합) ──

export type LibrarySectionType = 'CATEGORY' | 'CURATION' | 'DYNAMIC';
export type LibrarySectionLayoutHint =
    | 'CAROUSEL_1ROW'
    | 'CAROUSEL_2ROW'
    | 'HIGHLIGHTED_GRID'
    | 'MEDIA_LIST';

export const LAYOUT_HINT_OPTIONS: LibrarySectionLayoutHint[] = [
    'CAROUSEL_1ROW',
    'CAROUSEL_2ROW',
    'HIGHLIGHTED_GRID',
    'MEDIA_LIST',
];

export interface SectionTranslation {
    lang: string;
    displayName: string;
}

export interface LibrarySection {
    id: number;
    type: LibrarySectionType;
    /** code는 생성 후 immutable */
    code: string;
    sortOrder: number;
    layoutHint: LibrarySectionLayoutHint;
    /** type=DYNAMIC일 때만 값 존재 */
    ruleCode: string | null;
    /** chip prefix 텍스트 기호(이모지). null/빈값 = 미표시 */
    icon: string | null;
    isActive: boolean;
    translations: SectionTranslation[];
    createdAt: string;
    updatedAt: string;
}

/** 라이브러리 응답에 임베드되는 섹션 참조 */
export interface LibrarySectionRefDto {
    id: number;
    code: string;
    /** 요청 lang(ko 기본) 번역명 */
    displayName: string;
}

export interface DynamicRuleItem {
    ruleCode: string;
    description: string;
}

export interface DynamicRulesResponse {
    rules: DynamicRuleItem[];
}

export interface CreateLibrarySectionRequest {
    type: LibrarySectionType;
    code: string;
    sortOrder?: number;
    layoutHint: LibrarySectionLayoutHint;
    /** type=DYNAMIC이면 필수 + 화이트리스트 */
    ruleCode?: string;
    /** chip prefix 텍스트 기호(이모지). 빈 문자열은 미설정으로 저장 */
    icon?: string;
    isActive: boolean;
    translations: SectionTranslation[];
}

export interface UpdateLibrarySectionRequest {
    sortOrder?: number;
    layoutHint?: LibrarySectionLayoutHint;
    ruleCode?: string;
    /** 빈 문자열 = 미설정. null로 클리어하려면 clearIcon=true 사용. */
    icon?: string;
    clearIcon?: boolean;
    isActive?: boolean;
    translations?: SectionTranslation[];
}

export interface SectionOrderItem {
    id: number;
    sortOrder: number;
}

// ── 라이브러리 ──

export interface Library {
    id: number;
    wordbookId?: number;
    groupId?: number;
    itemType: LibraryItemType;
    title: string;
    description: string;
    thumbnailUrl?: string;
    headLang: string;
    meaningLang: string;
    /** 신규: 카테고리 섹션 (필수) */
    categorySection: LibrarySectionRefDto;
    /** 신규: 큐레이션 섹션 (옵셔널) */
    curationSection: LibrarySectionRefDto | null;
    /** 큐레이션 섹션 내 정렬 순서 */
    curationSortOrder: number | null;
    libraryType: LibraryType;
    tier: LibraryTier;
    wordCount: number;
    wordbookCount?: number;
    downloadCount: number;
    ratingAverage: number;
    ratingCount: number;
    isVisible: boolean;
    previewWords: PreviewWord[];
    groupWordbooks?: GroupWordbookInfo[];
    publishedAt?: string;
    createdAt: string;
    updatedAt: string;
}

export interface CategoryStat {
    /** 백엔드가 카테고리 식별자 문자열을 반환 */
    category: string;
    totalLibraries: number;
    visibleLibraries: number;
    totalDownloads: number;
    averageRating: number;
}
